import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Smartphone, ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button';
import { verificationService } from '../../services/verificationService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const SmsVerificationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get phone number and verificationId from navigation state
  const phoneNumber = location?.state?.phoneNumber || '';
  const initialVerificationId = location?.state?.verificationId || null;
  const stateUserId = location?.state?.userId || user?.id || null;
  
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const [redirecting, setRedirecting] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Countdown timer for code expiration (15 minutes)
  const [timeRemaining, setTimeRemaining] = useState(15 * 60); // 15 minutes in seconds

  // Redirect if no phone number provided
  useEffect(() => {
    if (!phoneNumber) {
      navigate('/login');
    }
  }, [phoneNumber, navigate]);

  // Countdown timer for expiration
  useEffect(() => {
    if (timeRemaining > 0 && !success) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeRemaining === 0) {
      setError('Verification code has expired. Please request a new code.');
    }
  }, [timeRemaining, success]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone?.replace(/\D/g, '');
    if (cleaned?.length === 11 && cleaned?.startsWith('1')) {
      return `+1 (${cleaned?.slice(1, 4)}) ${cleaned?.slice(4, 7)}-${cleaned?.slice(7)}`;
    } else if (cleaned?.length === 10) {
      return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3, 6)}-${cleaned?.slice(6)}`;
    }
    return phone;
  };

  // Handle code input
  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^[0-9]$/?.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      nextInput?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode?.every((digit) => digit !== '') && index === 5) {
      handleVerify(newCode?.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !code?.[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    
    if (/^[0-9]{6}$/?.test(pastedData)) {
      const newCode = pastedData?.split('');
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  // Verify code
  const handleVerify = async (codeString = null) => {
    const verificationCode = codeString || code?.join('');
    
    if (verificationCode?.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (!verificationId) {
      setError('Verification session expired. Please request a new code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verificationService?.verifyCode(verificationId, verificationCode, stateUserId);
      setSuccess(true);
      
      // Fetch user role from user_profiles and redirect to role-based dashboard
      setRedirecting(true);
      await redirectToRoleBasedDashboard();
    } catch (err) {
      console.error('Verification error:', err);
      setAttempts((prev) => prev + 1);
      
      if (err?.message?.includes('expired')) {
        setError('Verification code has expired. Please request a new code.');
        setTimeRemaining(0);
      } else if (err?.message?.includes('Maximum')) {
        setError('Maximum verification attempts exceeded. Please request a new code.');
      } else if (err?.message?.includes('Invalid')) {
        setError(`Invalid code. ${maxAttempts - attempts - 1} attempts remaining.`);
      } else {
        setError(err?.message || 'Verification failed. Please try again.');
      }
      
      // Clear code inputs on error
      setCode(['', '', '', '', '', '']);
      document.getElementById('code-input-0')?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Role-based dashboard redirect
  const redirectToRoleBasedDashboard = async () => {
    try {
      // Get current user
      const { data: { user: currentUser } } = await supabase?.auth?.getUser();
      
      if (!currentUser) {
        navigate('/login');
        return;
      }

      // Fetch user profile to get role
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', currentUser?.id)
        ?.single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        // Default to operations overview if profile fetch fails
        setTimeout(() => navigate('/ipm-operations-overview'), 2000);
        return;
      }

      const role = profile?.role || 'employee';
      setUserRole(role);

      // Role-based dashboard mapping
      const dashboardMap = {
        'company_admin': '/admin-dashboard',
        'supervisor': '/supervisor-alert-management-dashboard',
        'manager': '/team-performance-hub',
        'employee': '/ipm-operations-overview'
      };

      const targetDashboard = dashboardMap?.[role] || '/ipm-operations-overview';

      // Redirect after 2 seconds with loading state
      setTimeout(() => {
        navigate(targetDashboard);
      }, 2000);
    } catch (err) {
      console.error('Redirect error:', err);
      // Fallback to default dashboard
      setTimeout(() => navigate('/ipm-operations-overview'), 2000);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      const result = await verificationService?.sendSmsVerification(phoneNumber, stateUserId);
      setVerificationId(result?.verificationId);
      setResendCooldown(60); // 60 second cooldown
      setTimeRemaining(15 * 60); // Reset timer to 15 minutes
      setAttempts(0); // Reset attempts
      setCode(['', '', '', '', '', '']); // Clear code inputs
    } catch (err) {
      console.error('Resend error:', err);
      setError(err?.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit phone number
  const handleEditPhone = () => {
    navigate('/login', { state: { mode: 'signup', editPhone: true } });
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600 animate-bounce" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Phone Verified!</h2>
            <p className="text-gray-600">
              Your phone number has been successfully verified.
            </p>
            {userRole && (
              <p className="text-sm text-gray-500 mt-2">
                Role: <span className="font-medium text-green-600 capitalize">{userRole?.replace('_', ' ')}</span>
              </p>
            )}
          </div>
          
          <div className="space-y-3">
            {redirecting ? (
              <>
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                  <span>Redirecting to your dashboard...</span>
                </div>
              </>
            ) : (
              <Button
                onClick={() => navigate('/ipm-operations-overview')}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Go to Dashboard
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600 p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Smartphone className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Your Phone</h2>
          <p className="text-gray-600 text-sm">
            We've sent a 6-digit verification code to
          </p>
          <div className="flex items-center justify-center mt-2 space-x-2">
            <p className="text-gray-800 font-medium">{formatPhoneNumber(phoneNumber)}</p>
            <button
              onClick={handleEditPhone}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Timer */}
        <div className="mb-6 text-center">
          <p className="text-sm text-gray-600">
            Code expires in: <span className="font-medium text-green-600">{formatTime(timeRemaining)}</span>
          </p>
        </div>

        {/* Code Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
            Enter Verification Code
          </label>
          <div className="flex justify-center space-x-2" onPaste={handlePaste}>
            {code?.map((digit, index) => (
              <input
                key={index}
                id={`code-input-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e?.target?.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                disabled={loading}
              />
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Verify Button */}
        <Button
          onClick={() => handleVerify()}
          disabled={loading || code?.some((digit) => digit === '')}
          className="w-full mb-4 bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Verifying...</span>
            </div>
          ) : (
            'Verify Code'
          )}
        </Button>

        {/* Resend Code */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Didn't receive the code?
          </p>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
            className="text-green-600 hover:text-green-700 font-medium text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </div>

        {/* Attempts Counter */}
        {attempts > 0 && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Attempts: {attempts} / {maxAttempts}
            </p>
          </div>
        )}

        {/* Back to Login */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-800 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Login</span>
          </button>
        </div>

        {/* SMS Compliance Notice */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 text-center">
            By verifying your phone, you agree to receive SMS messages from GROW GUARDIAN LLC. 
            Message frequency varies. Message & data rates may apply. 
            Reply STOP to opt out or HELP for help.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmsVerificationScreen;